import React, { useState, useCallback } from 'react';
import { Card, Table, Button, message, DatePicker } from 'antd';
import { SearchOutlined, ExportOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { PAGE_QUERY_BILLING_PERIOD_SUMMARY } from '../../../graphql/queries/settlement/billingPeriodSummaryQueries';
import { EXPORT_BILLING_PERIOD_SUMMARY } from '../../../graphql/mutations/settlement/billingPeriodSummaryMutations';
import { ChannelSelectFilter } from '../../../components/settlement/ChannelSelectFilter';
import TablePaginationBar from '../../../components/TablePaginationBar';
import ParcelsDownloadModal from './components/ParcelsDownloadModal';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type {
    BillingPeriodSummaryFilterInput,
} from '../../../graphql/types/settlement/billingPeriodSummaryTypes';
import { getCurrencyName } from '../../../utils/settlementAmountFormatter';
import { resolvePricingSchemeMessage } from '../../../utils/graphqlUtils';
const { RangePicker } = DatePicker;

const BillPeriodEstimatedIncomePage: React.FC = () => {
    const [filters, setFilters] = useState<BillingPeriodSummaryFilterInput>({});
    const [tempFilters, setTempFilters] = useState<BillingPeriodSummaryFilterInput>({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    const { data, loading } = useQuery(PAGE_QUERY_BILLING_PERIOD_SUMMARY, {
        variables: {
            filters: {
                clientIds: filters.clientId ? [filters.clientId] : undefined,
                billingPeriodStart: filters.billingPeriodStart,
                billingPeriodEnd: filters.billingPeriodEnd,
            },
            page,
            pageSize,
        },
        fetchPolicy: 'cache-and-network',
    });

    const [exportBillingPeriodSummary] = useMutation(EXPORT_BILLING_PERIOD_SUMMARY);

    const handleSearch = useCallback(() => {
        setFilters(tempFilters);
        setPage(1);
    }, [tempFilters]);

    const handleReset = () => {
        setTempFilters({});
        setFilters({});
        setPage(1);
    };

    const handleExport = async () => {
        try {
            const result = await exportBillingPeriodSummary({
                variables: {
                    filters: {
                        clientIds: filters.clientId ? [filters.clientId] : undefined,
                        billingPeriodStart: filters.billingPeriodStart,
                        billingPeriodEnd: filters.billingPeriodEnd,
                    },
                },
            });

            const exportResult = result.data?.exportBillingPeriodSummary;
            const fileName = exportResult?.data
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            } else {
                message.error(resolvePricingSchemeMessage(exportResult, '导出失败'));
            }
        } catch (error) {
            message.error('导出失败: ' + (error as Error).message);
        }
    };

    const columns = [
        {
            title: '客户ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 120,
        },
        {
            title: '客户名称',
            dataIndex: 'clientName',
            key: 'clientName',
            width: 150,
            fixed: 'left' as const,
        },
        {
            title: '日期',
            dataIndex: 'billingPeriod',
            key: 'billingPeriod',
            width: 120,
            render: (period: number) => {
                const dateStr = period.toString();
                if (dateStr.length === 8) {
                    return `${dateStr.substring(0, 4)}.${dateStr.substring(4, 6)}.${dateStr.substring(6, 8)}`;
                }
                return period;
            },
        },
        {
            title: '币种',
            dataIndex: 'currency',
            key: 'currency',
            width: 80,
            render: (currency: string) => getCurrencyName(currency),
        },
        {
            title: '订单总数',
            dataIndex: 'orderCount',
            key: 'orderCount',
            width: 100,
        },
        {
            title: '预估收入总额',
            dataIndex: 'amount',
            key: 'amount',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '时区',
            dataIndex: 'timezone',
            key: 'timezone',
            width: 150,
        },
        {
            title: '客户基础运费',
            dataIndex: 'baseFreight',
            key: 'baseFreight',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户基础运费-调整',
            dataIndex: 'baseFreightAdj',
            key: 'baseFreightAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '其他调增',
            dataIndex: 'otherAdj',
            key: 'otherAdj',
            width: 120,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '其他调增-调整',
            dataIndex: 'otherAdjAdj',
            key: 'otherAdjAdj',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户超长费',
            dataIndex: 'overLengthFee',
            key: 'overLengthFee',
            width: 130,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户超长费调整',
            dataIndex: 'overLengthFeeAdj',
            key: 'overLengthFeeAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户超重费',
            dataIndex: 'overWeightFee',
            key: 'overWeightFee',
            width: 130,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户超重费-调整',
            dataIndex: 'overWeightFeeAdj',
            key: 'overWeightFeeAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '配送费(TT)',
            dataIndex: 'deliveryFeeTt',
            key: 'deliveryFeeTt',
            width: 130,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '配送费(TT)-调整',
            dataIndex: 'deliveryFeeTtAdj',
            key: 'deliveryFeeTtAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '退货费（TT）',
            dataIndex: 'returnFeeTt',
            key: 'returnFeeTt',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '理赔调整',
            dataIndex: 'claimAdj',
            key: 'claimAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '理赔调整-调整',
            dataIndex: 'claimAdjAdj',
            key: 'claimAdjAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '其他调减',
            dataIndex: 'otherDecrease',
            key: 'otherDecrease',
            width: 120,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '其他调减-调整',
            dataIndex: 'otherDecreaseAdj',
            key: 'otherDecreaseAdj',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
    ];

    return (
        <div className="p-6 space-y-4">
            <Card title="筛选条件">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <ChannelSelectFilter
                            stacked
                            label="客户名称"
                            placeholder="搜索客户..."
                            value={tempFilters.clientId != null ? String(tempFilters.clientId) : undefined}
                            onChange={(val) => {
                                setTempFilters((prev) => ({
                                    ...prev,
                                    clientId: val ? Number(val) : undefined,
                                }));
                            }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">时间范围</label>
                        <RangePicker
                            className="w-full"
                            value={
                                tempFilters.billingPeriodStart && tempFilters.billingPeriodEnd
                                    ? [
                                        dayjs(tempFilters.billingPeriodStart.toString(), 'YYYYMMDD'),
                                        dayjs(tempFilters.billingPeriodEnd.toString(), 'YYYYMMDD'),
                                    ] as [Dayjs, Dayjs]
                                    : null
                            }
                            onChange={(dates) => {
                                if (dates?.[0] && dates?.[1]) {
                                    setTempFilters((prev) => ({
                                        ...prev,
                                        billingPeriodStart: parseInt(dates[0]!.format('YYYYMMDD')),
                                        billingPeriodEnd: parseInt(dates[1]!.format('YYYYMMDD')),
                                    }));
                                } else {
                                    setTempFilters((prev) => ({
                                        ...prev,
                                        billingPeriodStart: undefined,
                                        billingPeriodEnd: undefined,
                                    }));
                                }
                            }}
                            format="YYYY-MM-DD"
                        />
                    </div>
                    <div className="flex items-end gap-2">
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            className="flex-1"
                        >
                            查询
                        </Button>
                        <Button onClick={handleReset} className="flex-1">
                            重置
                        </Button>
                    </div>
                    <div className="flex items-end">
                        <Button
                            type="default"
                            icon={<ExportOutlined />}
                            onClick={handleExport}
                            disabled={!data?.pageQueryBillingPeriodSummary?.total}
                        >
                            导出
                        </Button>
                    </div>
                </div>
            </Card>

            <Card title="列表">
                <Table
                    columns={columns}
                    dataSource={data?.pageQueryBillingPeriodSummary?.list || []}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 3260 }}
                    className="mb-4"
                />

                <TablePaginationBar
                    total={data?.pageQueryBillingPeriodSummary?.total || 0}
                    current={page}
                    pageSize={pageSize}
                    onChange={(newPage, newPageSize) => {
                        setPage(newPage);
                        setPageSize(newPageSize);
                    }}
                />
            </Card>

            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default BillPeriodEstimatedIncomePage;
